import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import moment from 'moment';
import useLocales from 'hooks/useLocales';

export type ExplanatoryCallStatus = 'CallRequest' | 'InProgress' | 'Resolved' | 'Cancelled';

type ExplanatoryCall = {
  id?: string;
  status?: ExplanatoryCallStatus | string;
  meetingTime?: string;
} | null;

type CallRequestCellProps = {
  explanatoryCall?: ExplanatoryCall;
  onClick?: () => void;
};

const STATUS_STYLES: Record<string, { bg: string; border: string; color: string }> = {
  CallRequest: {
    bg: '#FFF8E7',
    border: '#C9A84C',
    color: '#B8860B'
  },
  InProgress: {
    bg: '#E8F4FD',
    border: '#5BA3D9',
    color: '#2B7BBF'
  },
  Resolved: {
    bg: '#E8F8EF',
    border: '#5CB87A',
    color: '#2E7D4F'
  },
  Cancelled: {
    bg: '#FDECEC',
    border: '#E57373',
    color: '#C62828'
  }
};

export { STATUS_STYLES };

const TIME_STYLE = {
  bg: '#FFF8E7',
  border: '#C9A84C',
  color: '#B8860B'
};

const Pill = ({
  children,
  bg,
  border,
  color
}: {
  children: ReactNode;
  bg: string;
  border: string;
  color: string;
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1.25,
      py: 0.4,
      borderRadius: 20,
      bgcolor: bg,
      border: `1px solid ${border}`,
      color,
      whiteSpace: 'nowrap',
      lineHeight: 1.2
    }}
  >
    {children}
  </Box>
);

function formatMeetingTime(meetingTime?: string) {
  if (!meetingTime) return null;
  const parsed = moment(meetingTime, ['HH:mm:ss', 'HH:mm', moment.ISO_8601], true);
  if (!parsed.isValid()) {
    const fallback = moment(meetingTime);
    return fallback.isValid() ? fallback.locale('en').format('h:mm A') : meetingTime;
  }
  return parsed.locale('en').format('h:mm A');
}

export default function CallRequestCell({ explanatoryCall, onClick }: CallRequestCellProps) {
  const { translate } = useLocales();

  if (!explanatoryCall?.status && !explanatoryCall?.meetingTime) {
    return (
      <Typography component="span" color="text.secondary">
        —
      </Typography>
    );
  }

  const statusKey = explanatoryCall?.status || '';
  const statusStyle = STATUS_STYLES[statusKey] || STATUS_STYLES.CallRequest;
  const formattedTime = formatMeetingTime(explanatoryCall?.meetingTime);

  return (
    <Stack
      spacing={0.75}
      alignItems="flex-start"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      sx={{ cursor: onClick ? 'pointer' : 'default', width: 'fit-content' }}
    >
      {formattedTime && (
        <Pill bg={TIME_STYLE.bg} border={TIME_STYLE.border} color={TIME_STYLE.color}>
          <AccessTime sx={{ fontSize: 14 }} />
          <Typography component="span" variant="caption" fontWeight={600} color="inherit">
            {formattedTime}
          </Typography>
        </Pill>
      )}

      {statusKey && (
        <Pill bg={statusStyle.bg} border={statusStyle.border} color={statusStyle.color}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: statusStyle.color,
              flexShrink: 0
            }}
          />
          <Typography component="span" variant="caption" fontWeight={600} color="inherit">
            {translate(statusKey)}
          </Typography>
        </Pill>
      )}
    </Stack>
  );
}
